// src/familyLogin.js
// Families log in with their email and their account's permanent 6-digit
// code, which the studio WhatsApps them once:
//   1. checkLoginEmail(email)       — is it an account? (Cloud Function)
//   2. verifyLoginCode(email, code) — the right code signs this device in
//   requestLoginCode(email)         — asks a teacher to WhatsApp the code from
//                                     Logins (automatic the first time, or
//                                     "Forgot your code?")
// The sign-in is a real Firebase login marked as a family (uid family_<accountId>),
// which Firebase keeps on this device until they log out.
import { httpsCallable } from "firebase/functions";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { collection, deleteDoc, doc, query, where } from "firebase/firestore";
import { getDoc, getDocs } from "./queryMonitor"; // timed reads (Billing → Performance)
import { auth, db, functions } from "./firebase";
import { setClientEmail, setClientAccount, getClientEmail, getClientAccount, findAccountByEmail } from "./clientIdentity";

const PENDING_KEY = "dance_booking_login_request";
const CONFIRMED_KEY = "dance_booking_confirmed";

export const isFamilyUser = (user) => !!user && String(user.uid || "").startsWith("family_");
export const isTeacherUser = (user) => !!user && !isFamilyUser(user);

// The request waiting for its code, so the code box survives closing the app.
export function getPendingLogin() {
  try { return JSON.parse(localStorage.getItem(PENDING_KEY)); } catch { return null; }
}
function setPendingLogin(pending) {
  try {
    if (pending) localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    else localStorage.removeItem(PENDING_KEY);
  } catch { /* storage unavailable — the code box just won't survive a reload */ }
}

// Cloud Function errors carry a friendly message; fall back for network trouble.
function friendly(err) {
  return err?.message && err.code !== "functions/internal" ? err.message : "Something went wrong — please try again.";
}

// Returns { found: false } for an email we don't know; otherwise remembers
// the email as the login in progress ({ email, phoneHint, hasPhone, requested }).
// A family that has never been sent its code is asked for it straight away —
// they don't need to tap anything; one already waiting isn't asked twice.
export async function checkLoginEmail(email) {
  const clean = (email || "").trim().toLowerCase();
  let data;
  try {
    ({ data } = await httpsCallable(functions, "checkLoginEmail")({ email: clean }));
  } catch (err) {
    throw new Error(friendly(err));
  }
  if (!data.found) return { found: false };
  const pending = { email: clean, phoneHint: data.phoneHint, hasPhone: data.hasPhone, requested: !!data.waiting };
  setPendingLogin(pending);
  if (!data.hasCode && !data.waiting) return { found: true, ...(await requestLoginCode(clean)) };
  return { found: true, ...pending };
}

// Asks the studio to WhatsApp the family their code.
export async function requestLoginCode(email) {
  try {
    await httpsCallable(functions, "requestLoginCode")({ email });
    const pending = { ...(getPendingLogin() || { email }), requested: true };
    setPendingLogin(pending);
    return pending;
  } catch (err) {
    throw new Error(friendly(err));
  }
}

// Checks the code and, if right, logs this device in. Returns the account.
export async function verifyLoginCode(email, code) {
  let data;
  try {
    ({ data } = await httpsCallable(functions, "verifyLoginCode")({ email, code }));
  } catch (err) {
    throw new Error(friendly(err));
  }
  await signInWithCustomToken(auth, data.token);
  setPendingLogin(null);
  setClientEmail(data.email);
  setClientAccount(data.account);
  try { localStorage.setItem(CONFIRMED_KEY, "1"); } catch { /* ignore */ }
  return data.account;
}

// Teachers deleting an account: its login code and any login requests go too.
export async function deleteAccountLogin(accountId) {
  const reqs = await getDocs(query(collection(db, "loginRequests"), where("accountId", "==", accountId)));
  for (const d of reqs.docs) await deleteDoc(d.ref);
  await deleteDoc(doc(db, "accountPrivate", accountId));
}

export function cancelPendingLogin() {
  setPendingLogin(null);
}

// Forgets the family on this device (and signs out a family login, never a teacher's).
export async function familyLogout() {
  setPendingLogin(null);
  setClientEmail("");
  setClientAccount(null);
  try { localStorage.removeItem(CONFIRMED_KEY); } catch { /* ignore */ }
  if (isFamilyUser(auth.currentUser)) await signOut(auth);
}

// Run once when the app starts, before any page reads who's on this device.
// Families linked to an account must have logged in with a code — once per
// device; Firebase then keeps that login, so they aren't asked again:
//   - code login present → keep it (and restore the remembered family if the
//     device lost it but kept the login)
//   - account remembered without a code login (set up before codes existed,
//     or an email that has since become an account) → forget it, so Home asks
//     for the email and code
//   - teachers' devices are left alone, so their previews keep working
export async function reconcileFamilySession(user) {
  if (isTeacherUser(user)) return;
  if (isFamilyUser(user)) {
    const accountId = user.uid.slice("family_".length);
    const remembered = getClientAccount();
    if (remembered?.id === accountId && getClientEmail()) return;
    const snap = await getDoc(doc(db, "accounts", accountId));
    if (!snap.exists()) { await familyLogout(); return; }
    const account = { id: snap.id, ...snap.data() };
    setClientEmail(account.email || getClientEmail());
    setClientAccount(account);
    try { localStorage.setItem(CONFIRMED_KEY, "1"); } catch { /* ignore */ }
    return;
  }
  const email = getClientEmail();
  if (getClientAccount()) { await familyLogout(); return; }
  if (email && await findAccountByEmail(email).catch(() => null)) await familyLogout();
}
